import { DialogActions as MuiDialogActions } from '@material-ui/core';
import { useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { useMemo } from 'react';
import {
  MutationDialog,
  MutationDialogContent,
} from 'shared/form/MutationDialog';
import { joinStrings } from 'shared/utils/StringUtils';
import { useLoadDetails, useLoadsCache } from '../data/LoadsAPI';
import { useReturnVehicle } from '../data/VehiclesAPI';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';
import styled from 'styled-components';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { useNavigate } from 'react-router-dom';
import { useSubhaulLoad } from '../view/useSubhaulLoad';

const DialogActions = styled(MuiDialogActions)`
  padding-right: 0;
`;

interface ReturnVehicleDialogProps {
  loadGUID: string;
  vehicleGUID?: string;
  onDismiss: () => void;
  onSubmitSuccess: () => void;
}

function ReturnVehicleDialogContent({
  loadGUID,
  onDismiss,
  vehicleGUID,
  onSubmitSuccess,
}: ReturnVehicleDialogProps) {
  const navigate = useNavigate();
  const { isSubhauled } = useSubhaulLoad();
  const { data } = useLoadDetails(loadGUID, { isSubhauled });
  const { addSnackbar, clearStack } = useSnackbarStack();
  const { invalidateLoads } = useLoadsCache();

  const vehicle = useMemo(
    () => data?.vehicles.find((v) => v.guid === vehicleGUID),
    [data?.vehicles, vehicleGUID],
  );

  const vehicleName = useMemo(
    () =>
      vehicle && joinStrings(' ', vehicle.year, vehicle.make, vehicle.model),
    [vehicle],
  );

  const returnVehicle = useReturnVehicle(
    { vehicle_guid: vehicleGUID || '' },
    {
      onSuccess(res: APIResponse<{ shipment_guid: string }>) {
        invalidateLoads();
        onSubmitSuccess();
        addSnackbar(`${vehicleName} moved to Vehicles`, {
          variant: 'success',
          action: (
            <Button
              variant="inverted"
              onClick={() => {
                navigate(`/vehicles/${res.data.shipment_guid}`);
                clearStack();
              }}
            >
              Open Vehicles
            </Button>
          ),
        });
      },
      onError({ message }) {
        addSnackbar(
          message ||
            'Something went wrong while returning the vehicle, contact support',
        );
      },
    },
  );

  return (
    <MutationDialogContent
      onClose={onDismiss}
      title={`Move ${vehicleName || 'vehicle'} to Vehicles?`}
    >
      <b>{vehicleName}</b> will be removed from this load and moved back to
      Vehicles page. From there you will be able to cancel and delete this
      vehicle if needed.
      <DialogActions>
        <Button
          variant="neutral"
          onClick={onDismiss}
          disabled={returnVehicle.isPending}
        >
          Cancel
        </Button>

        <PermissionButton
          name="loads"
          permission="can_manage"
          type="submit"
          disabled={returnVehicle.isPending}
          onClick={() => {
            returnVehicle.mutate(loadGUID);
          }}
        >
          Move to Vehicles
        </PermissionButton>
      </DialogActions>
    </MutationDialogContent>
  );
}

export function ReturnVehicleDialog(props: ReturnVehicleDialogProps) {
  return (
    <MutationDialog
      maxWidth="sm"
      fullWidth={true}
      onClose={props.onDismiss}
      open={!!props.vehicleGUID}
    >
      <ReturnVehicleDialogContent {...props} />
    </MutationDialog>
  );
}
